import { create, StoreApi, UseBoundStore } from 'zustand';

import { DatasetState, APIRobotInfo } from '@mygpt/app/apis';
import { DatasetsInfoWithProgress } from '@mygpt/app/datasets/services';
import { BotId } from '@mygpt/types';

interface BotStore {
  botId: BotId;
  robotInfo?: APIRobotInfo;
}

export const botStore = create<BotStore>(() => ({
  botId: '',
  robotInfo: undefined,
}));

const botWeakMap = new WeakMap<UseBoundStore<StoreApi<BotStore>>, string>();
export const Bot = {
  setDefaultBotId: (id?: BotId) => {
    if (!id) botWeakMap.delete(botStore);
    else botWeakMap.set(botStore, id);
  },
  getDefaultBotId: () => {
    return botWeakMap.get(botStore);
  },
  delDefaultBotId: () => {
    botWeakMap.delete(botStore);
  },
};

// bot信息，包括bot关联的dataset
interface BotStoreStatus {
  robotInfo: APIRobotInfo | undefined;
  setRobotInfo: (robotInfo: APIRobotInfo) => void;
  linkedDataset: DatasetsInfoWithProgress[];
  // 最近一次获取的训练进度，按 dataset_id 索引。
  // 持久保存在 store 中，保证 setLinkedDataset 与 setDatasetStatus 无论先后执行，
  // 列表数据都能合并上训练统计（修复首屏 Navbar 与 Embeddings 并发拉取列表时，
  // 后到的裸列表覆盖已合并统计导致统计显示为 "-" 的竞态问题）
  datasetProgress: Record<string, DatasetState>;
  setLinkedDataset: (linkedDataset: DatasetsInfoWithProgress[]) => void;
  setDatasetStatus: (datasetProgress: Record<string, DatasetState>) => void;
}

// 将训练进度统计合并进 dataset 列表
const mergeDatasetProgress = (
  linkedDataset: DatasetsInfoWithProgress[],
  datasetProgress: Record<string, DatasetState>
): DatasetsInfoWithProgress[] => {
  return linkedDataset.map((dataset) => {
    const progress = datasetProgress[dataset.id];
    if (!progress) return dataset;

    const total = progress.datasource_statistical_map.total;
    return {
      ...dataset,
      total_resources: total.total,
      status: {
        learned_count: total.complete,
        learning_count: total.process,
        pending_count: total.ready,
        failed_count: total.fail + total.exceeded,
        unavailable_count: total.invalid || 0,
      },
    };
  });
};

export const useBotStore = create<BotStoreStatus>((set, get) => ({
  robotInfo: undefined,
  setRobotInfo: (robotInfo) => set({ robotInfo }),
  linkedDataset: [],
  datasetProgress: {},
  setLinkedDataset: (linkedDataset) =>
    set({ linkedDataset: mergeDatasetProgress(linkedDataset, get().datasetProgress) }),
  setDatasetStatus: (datasetProgress: Record<string, DatasetState>) =>
    set({
      datasetProgress,
      linkedDataset: mergeDatasetProgress(get().linkedDataset, datasetProgress),
    }),
}));
